import { errorMessage } from '@helpers/Constants';
import { Assets } from '@helpers/Constants/Assets';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { SubscriptionFooter } from '@components/SubscriptionFooter';

export const PaymentMethodUpdateConfirmation: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [verifyResponse, setVerifyResponse] = useState<{
    subscription?: { cardBrand?: string; cardLast4?: string };
  } | null>(null);

  useEffect(() => {
    handleVerifySubscription();
  }, []);

  /**
   * Verifies the user's subscription status using the stored session ID.
   *
   * @description
   * - Retrieves the `sessionId` from localStorage (typically created during checkout)
   * - Sends a verification request to the backend API
   * - Logs the response data on success
   * - Displays a generic error toast if the request fails
   *
   * @async
   * @function handleVerifySubscription
   * @returns {Promise<void>}
   *
   * @dependencies
   * - Axios (for API request)
   * - ApiEndpoints.VERIFY_SUBSCRIPTION (verification endpoint)
   * - showToastMessage (for error feedback)
   * - errorMessage.DEFAULT_ERROR_MESSAGE (fallback error message)
   *
   */
  const handleVerifySubscription = async () => {
    // Get the session from the local storage for verification
    const sessionId = localStorage.getItem('sessionId');
    try {
      const response = await Axios.post(ApiEndpoints.VERIFY_SUBSCRIPTION, {
        session_id: sessionId,
      });
      setVerifyResponse(response.data.data);
      console.log('response', response.data);
    } catch {
      showToastMessage(errorMessage.DEFAULT_ERROR_MESSAGE, 'error');
    }
  };

  /**
   * Handles post-subscription completion cleanup and navigation.
   *
   * @description
   * - Removes temporary checkout-related data from localStorage
   *   (`createCheckoutSession`, `sessionId`)
   * - Redirects the user to the subscription details page
   *
   * @returns {void}
   *
   * @example
   * handleSubscriptionCompletion();
   */
  const handleSubscriptionCompletion = (): void => {
    localStorage.removeItem('createCheckoutSession');
    localStorage.removeItem('sessionId');

    // navigate to subscription details
    navigate(FrontendRoutes.SUBSCRIPTION_DETAILS);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative px-4">
      <button
        onClick={handleSubscriptionCompletion}
        type="button"
        className="absolute top-4 right-4 text-xl text-gray-500 hover:text-gray-800"
      >
        ✕
      </button>
      <div className="flex-1" />

      {/* Main Content */}
      <div className="w-full max-w-md flex flex-col items-center gap-6 text-center flex-1">
        <div className="flex flex-col items-center gap-3 xl:mt-6 lg:mt-6">
          <span className="text-[#38B83F] text-4xl">
            Payment Method Updated!
          </span>
          <img
            src={Assets.CONFIRMATION_SUCCESS}
            alt="Success"
            className="w-24 h-24 object-contain"
          />
          <h2 className="text-xl text-[#7F7F7F] font-semibold font-manrope">
            Your Payment Method has been successfully changed
          </h2>
        </div>

        {/* Plan card */}
        <div
          className="w-full px-8 py-4"
          style={{
            borderWidth: '4px 2px 2px 2px',
            borderStyle: 'solid',
            borderImage:
              'linear-gradient(224.86deg, #73CCD8 4.87%, #2B6B9F 96.04%) 1',
          }}
        >
          <div className="flex flex-row items-center gap-4 pr-2 mt-6 whitespace-nowrap">
            <span className="text-[#292D32]">New Payment Method:</span>

            <div className="flex items-center gap-2.5">
              <span className="text-[#374151] font-medium">
                {verifyResponse?.subscription?.cardBrand} ending in ••••{' '}
                {verifyResponse?.subscription?.cardLast4}
              </span>
            </div>
          </div>

          <div className="mt-6 p-2 flex items-center justify-center bg-[#F8F8F8] text-[#170B00]">
            <span className="ml-2">
              Any pending charges will be deducted from this card.
            </span>
          </div>
        </div>

        {/* <button
          onClick={handleSubscriptionCompletion}
          type="submit"
          className="w-full px-6 py-2 mb-5 rounded-md font-medium text-white bg-primary hover:bg-dark-primary"
        >
          Continue and Set Up Store Configuration
        </button> */}
      </div>

      <div className="flex-1 mt-3" />
      {/* Footer */}
      <SubscriptionFooter isFromAppSettings={true} />
      <span className="mt-3"></span>
    </div>
  );
};
